import classNames from "classnames";
import SVGIcon from "components/v2/Images/SVGIcon";
import Popover from "components/v2/Popover";
import commonStyles from "components/v2/style.module.css";
import WebsocketNotifications from "components/v2/WebSocket/WebsocketNotifications";
import { NOTIFICATION_STATUS } from "constants/v2/notifications";
import { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { getNotifications } from "store/core/notifications/actions";
import {
  selectCancelledNotificationIds,
  selectCompletedNotificationIds,
  selectFailedNotificationIds,
  selectInProgressNotificationIds,
} from "store/core/notifications/selectors";
import { selectSelectedEntityId } from "store/entity/selectors";

import NotificationCard from "./NotificationCard";
import styles from "./style.module.css";

const StatusItem = ({ label, count, isActive, countColor, setActiveStatus }) => {
  return (
    <div className={classNames(styles.statusItem, { [styles.active]: isActive })} onClick={setActiveStatus}>
      {label}
      <svg xmlns="http://www.w3.org/2000/svg" width="3" height="2" viewBox="0 0 3 2" fill="none">
        <circle opacity="0.4" cx="1.9375" cy="1" r="1" fill={isActive ? "#343434" : "white"} />
      </svg>
      <span className={styles.statusItemCount} style={{ color: countColor }}>
        {count}
      </span>
    </div>
  );
};

const Notifications = ({
  entityId,
  getNotifications,
  inProgressNotificationIds,
  failedNotificationIds,
  completedNotificationIds,
  cancelledNotificationIds,
  isExpanded,
}) => {
  const [activeStatus, setActiveStatus] = useState(NOTIFICATION_STATUS.IN_PROGRESS);

  const notificationIds = useMemo(() => {
    if (activeStatus === NOTIFICATION_STATUS.IN_PROGRESS) {
      return inProgressNotificationIds;
    } else if (activeStatus === NOTIFICATION_STATUS.FAILED) {
      return failedNotificationIds;
    } else if (activeStatus === NOTIFICATION_STATUS.COMPLETED) {
      return completedNotificationIds;
    } else if (activeStatus === NOTIFICATION_STATUS.CANCELLED) {
      return cancelledNotificationIds;
    }
  }, [
    activeStatus,
    inProgressNotificationIds,
    failedNotificationIds,
    completedNotificationIds,
    cancelledNotificationIds,
  ]);

  const contentItems = useMemo(() => {
    return (
      <div className={styles.popoverItemContainer}>
        <div className={styles.statusContainer}>
          <StatusItem
            label="In Progress"
            count={inProgressNotificationIds.length}
            countColor="#FDAD31"
            isActive={activeStatus === NOTIFICATION_STATUS.IN_PROGRESS}
            setActiveStatus={() => setActiveStatus(NOTIFICATION_STATUS.IN_PROGRESS)}
          />
          <StatusItem
            label="Failed"
            count={failedNotificationIds?.length}
            countColor="#FD5050"
            isActive={activeStatus === NOTIFICATION_STATUS.FAILED}
            setActiveStatus={() => setActiveStatus(NOTIFICATION_STATUS.FAILED)}
          />
          <StatusItem
            label="Cancelled"
            count={cancelledNotificationIds?.length}
            countColor="#FD5050"
            isActive={activeStatus === NOTIFICATION_STATUS.CANCELLED}
            setActiveStatus={() => setActiveStatus(NOTIFICATION_STATUS.CANCELLED)}
          />
          <StatusItem
            label="Completed"
            count={completedNotificationIds?.length}
            countColor="#21B23E"
            isActive={activeStatus === NOTIFICATION_STATUS.COMPLETED}
            setActiveStatus={() => setActiveStatus(NOTIFICATION_STATUS.COMPLETED)}
          />
        </div>
        <div className={classNames(styles.notificationContainer, commonStyles.scrollBar)}>
          {notificationIds?.length > 0 ? (
            notificationIds?.map((notificationId) => (
              <NotificationCard key={notificationId} notificationId={notificationId} />
            ))
          ) : (
            <div className={styles.emptyNotificationContainer}>🎉 Yay! You’re all caught up!</div>
          )}
        </div>
      </div>
    );
  }, [
    inProgressNotificationIds?.length,
    activeStatus,
    failedNotificationIds?.length,
    completedNotificationIds?.length,
    cancelledNotificationIds?.length,
    notificationIds,
  ]);

  const fetchNotifications = useCallback(async () => {
    try {
      await getNotifications({ entityId, payload: { headers: { entityId } } });
    } catch (error) {
      console.error(error);
    }
  }, [entityId, getNotifications]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  return (
    <>
      <Popover overlayClassName={styles.overlay} contentItems={contentItems} placement="right">
        <div className={styles.itemContainer} data-expanded={String(isExpanded)}>
          <SVGIcon src="components/v2/Sidebar/NavigationBar/notifications.svg" className={styles.icon} />
          <div data-testid="BottomNavigationBar-Notifications" className={styles.label}>
            Notifications
          </div>
        </div>
      </Popover>
      <WebsocketNotifications />
    </>
  );
};

const mapStateToProps = createStructuredSelector({
  entityId: selectSelectedEntityId,
  completedNotificationIds: selectCompletedNotificationIds,
  inProgressNotificationIds: selectInProgressNotificationIds,
  failedNotificationIds: selectFailedNotificationIds,
  cancelledNotificationIds: selectCancelledNotificationIds,
});

const mapDispatchToProps = {
  getNotifications,
};

export default connect(mapStateToProps, mapDispatchToProps)(Notifications);
